import React, { useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Dot } from 'recharts';
import { Typography } from 'components';

const { Text } = Typography;

interface HistoricalChartProps {
  data: any[];
  chartConfig: any;
  tooltipFormatter: any;
  hoveredItemKey: string | null;
  onDotClick: (item: any) => void;
  getItemKey: (item: any) => string;
}

export const HistoricalChart: React.FC<HistoricalChartProps> = React.memo(
  ({ data, chartConfig, tooltipFormatter, hoveredItemKey, onDotClick, getItemKey }) => {
    // Memoize dot render function to prevent recreation on every render
    const renderDot = useCallback(
      (props: any) => {
        const { key, ...restProps } = props;
        const isHovered = hoveredItemKey === getItemKey(props.payload);

        return (
          <Dot
            key={key}
            {...restProps}
            r={isHovered ? 10 : 7}
            fill={isHovered ? '#047857' : '#05796B'}
            stroke="white"
            strokeWidth={isHovered ? 2 : 1}
            style={{
              cursor: 'pointer',
              filter: isHovered
                ? 'drop-shadow(0px 3px 6px rgba(0,0,0,0.3))'
                : 'drop-shadow(0px 1px 3px rgba(0,0,0,0.1))',
            }}
            onClick={() => onDotClick(props.payload)}
          />
        );
      },
      [hoveredItemKey, onDotClick, getItemKey]
    );

    // Memoize active dot render function
    const renderActiveDot = useCallback(
      (props: any) => {
        const { key, ...restProps } = props;
        const isHovered = hoveredItemKey === getItemKey(props.payload);

        return (
          <Dot
            key={key}
            {...restProps}
            r={isHovered ? 12 : 9}
            fill={isHovered ? '#047857' : '#05796B'}
            stroke="white"
            strokeWidth={isHovered ? 3 : 2}
            style={{
              cursor: 'pointer',
              filter: isHovered
                ? 'drop-shadow(0px 4px 8px rgba(0,0,0,0.4))'
                : 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))',
            }}
            onClick={() => onDotClick(props.payload)}
          />
        );
      },
      [hoveredItemKey, onDotClick, getItemKey]
    );

    if (!data || data.length === 0) {
      return (
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            color: '#8c8c8c',
          }}
        >
          <div style={{ fontSize: '18px', marginBottom: '8px' }}>📊</div>
          <Text type="secondary" style={{ fontSize: '16px' }}>
            No Data Available
          </Text>
          <Text type="secondary" style={{ fontSize: '14px', marginTop: '4px' }}>
            Try selecting a different supplier or date range
          </Text>
        </div>
      );
    }

    return (
      <div style={{ height: '400px', width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis {...chartConfig.xAxisConfig} />
            <YAxis {...chartConfig.yAxisConfig} />
            <Tooltip formatter={tooltipFormatter} labelFormatter={() => ''} isAnimationActive={false} />
            <Line
              type="monotone"
              dataKey="price"
              stroke="#05796B"
              strokeWidth={2}
              dot={renderDot}
              activeDot={renderActiveDot}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }
);

HistoricalChart.displayName = 'HistoricalChart';
